import { useEffect, useMemo, useState } from "react";
import { fetchWithToken } from "../../helpers/fetch";

export default function DashboardSAT({
  data = [],
  fetchOnOpen = true,
  thresholdSeconds = 1.0,
}) {
  const [remoteData, setRemoteData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [visible, setVisible] = useState(false);

  // Fade-in
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 10);
    return () => clearTimeout(t);
  }, []);
  // Fetch when mounted (modal opens and mounts the component)
  useEffect(() => {
    if (!fetchOnOpen) return;
    let cancelled = false;
    let timeoutId;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await fetchWithToken("api/health-sat/history/0");
        if (!resp || !resp.ok)
          throw new Error(`HTTP ${resp ? resp.status : "no response"}`);
        const body = await resp.json();
        const mapped = (body || []).map((item) => {
          const status = item.status ?? item.code ?? 1;
          const durationSeconds = Number(
            item.durationSeconds ?? item.duration ?? item.elapsed ?? 0
          );
          const message = item.message ?? item.mensaje ?? "";
          const colorCode =
            item.colorCode ??
            item.color ??
            (status === 0 ? "#4CAF50" : "#F44336");
          const monitored =
            item.monitored ??
            item.createdAt ??
            item.timestamp ??
            new Date().toISOString();
          return { status, durationSeconds, message, colorCode, monitored };
        });
        if (!cancelled) setRemoteData(mapped);
      } catch (err) {
        if (!cancelled) setError(err.message ?? String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    timeoutId = setTimeout(() => {
      if (!cancelled) load();
    }, 1000);

    return () => ((cancelled = true), clearTimeout(timeoutId));
  }, [fetchOnOpen]);

  const effectiveData = remoteData && remoteData.length > 0 ? remoteData : data; //&& data.length > 0
  // ? data
  // : satSampleData;

  const sorted = useMemo(() => {
    return (effectiveData || [])
      .slice()
      .sort((a, b) => new Date(b.monitored) - new Date(a.monitored));
  }, [effectiveData]);

  if (!sorted || sorted.length === 0) {
    return (
      <div
        style={{
          padding: 16,
          minWidth: 480,
          opacity: visible ? 1 : 0,
          transition: "opacity 220ms ease",
        }}
      >
        <h3>Monitoreo SAT</h3>
        {loading ? (
          <div>Cargando datos del SAT...</div>
        ) : error ? (
          <div style={{ color: "#c62828" }}>Error al cargar datos: {error}</div>
        ) : (
          <div>No hay datos disponibles</div>
        )}
      </div>
    );
  }

  const last = sorted[0];
  const total = sorted.length;
  const success = sorted.filter((d) => d.status === 0).length;
  const availability = ((success / Math.max(total, 1)) * 100).toFixed(1);

  // Ejes y ticks
  const maxDuration = Math.max(...sorted.map((d) => d.durationSeconds), 0.001);
  const niceMaxDuration = Math.max(1, Math.ceil(maxDuration * 1.2 * 100) / 100);
  const tickCount = 4;
  const yTicks = Array.from({ length: tickCount + 1 }, (_, i) =>
    parseFloat(((i * niceMaxDuration) / tickCount).toFixed(2))
  );

  const reversed = sorted.slice().reverse();
  const xLabels = [];
  if (reversed.length > 0) {
    xLabels.push(0);
    if (reversed.length > 2)
      xLabels.push(Math.floor((reversed.length - 1) / 2));
    if (reversed.length > 1) xLabels.push(reversed.length - 1);
  }

  return (
    <div
      style={{
        padding: 20,
        minWidth: 760,
        fontFamily: "Roboto, Arial, sans-serif",
        backgroundColor: "#3e992794",
        opacity: visible ? 1 : 0,
        transition: "opacity 220ms ease",
      }}
    >
      <h2
        style={{
          fontSize: 28,
          color: "#222",
          fontWeight: 800,
          marginTop: 0,
          marginBottom: 16,
          textAlign: "center",
        }}
      >
        Monitoreo del Portal SAT
      </h2>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "240px 1fr",
          gap: 16,
          alignItems: "start",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div
            style={{
              background: "#fff",
              padding: 12,
              borderRadius: 10,
              boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
            }}
          >
            <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                marginBottom: 8,
                textAlign: "center",
                color: "#666",
              }}
            >
              Estado actual
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: 8,
              }}
            >
              <div
                style={{
                  padding: "8px 12px",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  whiteSpace: "nowrap",
                  borderRadius: 6,
                  background: last.status === 0 ? "#e8f5e9" : "#ffebee",
                  color: last.status === 0 ? "#2e7d32" : "#c62828",
                  fontWeight: 800,
                  fontSize: 28,
                }}
              >
                {last.status === 0 ? "OPERATIVO" : "NO DISPONIBLE"}
              </div>
            </div>
            <p
              style={{
                marginTop: 10,
                color: "#444",
                fontSize: 13,
                textAlign: "justify",
              }}
            >
              {last.message}
            </p>
          </div>

          <div
            style={{
              background: "#fff",
              padding: 10,
              borderRadius: 10,
              boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
              maxHeight: 500,
            }}
          >
            <div style={{ fontSize: 14, fontWeight: 700, textAlign: "center" }}>
              Historial de monitoreo
            </div>
            <div
              style={{
                marginTop: 8,
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              {sorted.slice(0, 12).map((d, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div style={{ fontSize: 12 }}>
                    {new Date(d.monitored).toLocaleString()}
                  </div>
                  <div style={{ marginLeft: 8 }}>
                    <span
                      style={{
                        display: "inline-block",
                        minWidth: 36,
                        padding: "4px 8px",
                        borderRadius: 6,
                        background: d.status === 0 ? "#4CAF50" : "#F44336",
                        color: "#fff",
                        fontWeight: 700,
                        fontSize: 12,
                      }}
                    >
                      {d.status === 0 ? "OK" : "ERR"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", gap: 12 }}>
            <div
              style={{
                flex: 1,
                background: "#fff",
                padding: 14,
                borderRadius: 10,
                boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
              }}
            >
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#666",
                  textAlign: "center",
                }}
              >
                Disponibilidad
              </div>
              <div
                style={{
                  fontSize: 36,
                  color: "#1565c0",
                  textAlign: "center",
                  fontWeight: 800,
                }}
              >
                {availability}%
              </div>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  marginBottom: 8,
                  textAlign: "center",
                  color: "#666",
                }}
              >
                de {total} mediciones
              </div>
            </div>

            <div
              style={{
                width: 180,
                background: "#fff",
                padding: 14,
                borderRadius: 10,
                boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
              }}
            >
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  marginBottom: 8,
                  textAlign: "center",
                  color: "#666",
                }}
              >
                Última duración
              </div>
              <div
                style={{
                  fontSize: 36,
                  color: "#222",
                  fontWeight: 800,
                  textAlign: "center",
                }}
              >
                {last.durationSeconds.toFixed(2)}s
              </div>
            </div>
          </div>

          <div
            style={{
              background: "#fff",
              padding: 12,
              borderRadius: 10,
              boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            }}
          >
            <div
              style={{
                fontSize: 14,
                fontWeight: 700,
                marginBottom: 8,
                textAlign: "center",
              }}
            >
              Duración de conexión (últimas pruebas)
            </div>
            <div style={{ width: "100%", height: 170 }}>
              <svg
                viewBox="0 0 100 48"
                preserveAspectRatio="none"
                style={{ width: "100%", height: "100%" }}
              >
                <g stroke="#f1f1f1" strokeWidth={0.6}>
                  {yTicks.map((val, i) => {
                    const y = 5 + (1 - val / niceMaxDuration) * 30;
                    return <line key={i} x1={0} y1={y} x2={100} y2={y} />;
                  })}
                </g>

                <g fill="#333" fontSize={7} textAnchor="start">
                  {yTicks.map((val, i) => {
                    const y = 5 + (1 - val / niceMaxDuration) * 30;
                    // Mostrar segundos como enteros (sin decimales)
                    const label = Math.round(val).toString();
                    return (
                      <text
                        key={i}
                        x={2}
                        y={y + 2}
                        style={{
                          fontFamily: "Roboto, Arial, sans-serif",
                          fontSize: 3,
                        }}
                      >
                        {label}s
                      </text>
                    );
                  })}
                </g>

                {(() => {
                  // build points from effective data (use only the last 5 measurements)
                  const chartPoints = reversed.slice(
                    Math.max(0, reversed.length - 12)
                  );
                  const pts = chartPoints.map((d, i, arr) => {
                    const x = (i / Math.max(arr.length - 1, 1)) * 100;
                    const y =
                      5 + (1 - d.durationSeconds / niceMaxDuration) * 30;
                    return { x, y, v: d.durationSeconds, t: d.monitored };
                  });

                  // helper: smooth path using quadratic curves between midpoints
                  const buildSmoothPath = (points) => {
                    if (!points || points.length === 0) return "";
                    if (points.length === 1)
                      return `M ${points[0].x},${points[0].y}`;
                    let d = `M ${points[0].x},${points[0].y} `;
                    for (let i = 1; i < points.length; i++) {
                      const prev = points[i - 1];
                      const curr = points[i];
                      const cx = (prev.x + curr.x) / 2;
                      const cy = (prev.y + curr.y) / 2;
                      d += `Q ${prev.x},${prev.y} ${cx},${cy} `;
                    }
                    // line to last point to finish
                    const last = points[points.length - 1];
                    d += `L ${last.x},${last.y}`;
                    return d;
                  };

                  const smooth = buildSmoothPath(pts);
                  const areaPath = smooth
                    ? `${smooth} L ${pts[pts.length - 1].x},44 L ${
                        pts[0].x
                      },44 Z`
                    : "";

                  // threshold line at 30 seconds
                  const thresholdValue = 30; // segundos
                  // compute y for threshold and clamp inside chart vertical bounds (5 .. 35)
                  let thresholdY =
                    5 + (1 - thresholdValue / niceMaxDuration) * 30;
                  if (thresholdY < 5) thresholdY = 5;
                  if (thresholdY > 35) thresholdY = 35;

                  // choose label step (now small, at most 5 points)
                  const step = 1;

                  return (
                    <g>
                      {/* amber filled area */}
                      {areaPath && (
                        <path
                          d={areaPath}
                          fill="rgba(255,193,7,0.18)"
                          stroke="none"
                        />
                      )}

                      {/* stroke line */}
                      {smooth && (
                        <path
                          d={smooth}
                          fill="none"
                          stroke="#ffb300"
                          strokeWidth={0.9}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      )}

                      {/* threshold line (30s) */}
                      <g>
                        <line
                          x1={0}
                          x2={100}
                          y1={thresholdY}
                          y2={thresholdY}
                          stroke="#d32f2f"
                          strokeWidth={0.6}
                          strokeDasharray="2,1"
                        />
                        <text
                          x={98}
                          y={Math.max(6, thresholdY - 1)}
                          textAnchor="end"
                          fontSize={5}
                          fill="#d32f2f"
                          style={{
                            fontFamily: "Roboto, Arial, sans-serif",
                            fontSize: 3,
                          }}
                        >
                          Umbral 30s
                        </text>
                      </g>

                      {/* points (small) */}
                      {pts.map((p, i) => (
                        <circle
                          key={i}
                          cx={p.x}
                          cy={p.y}
                          r={1.4}
                          fill="#ffb300"
                          stroke="#fff"
                          strokeWidth={0.25}
                        >
                          <title>{`${new Date(p.t).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}`}</title>
                        </circle>
                      ))}

                      {/* eje X oculto: removidas las etiquetas de hora para mantener la gráfica limpia */}
                    </g>
                  );
                })()}
              </svg>
            </div>
          </div>

          <div
            style={{
              background: "#fff",
              padding: 12,
              borderRadius: 10,
              boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
              maxHeight: 260,
              overflowY: "auto",
            }}
          >
            <div
              style={{
                fontSize: 14,
                fontWeight: 700,
                color: "#666",
                marginBottom: 8,
                textAlign: "center",
              }}
            >
              Historial de monitoreo
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {sorted.slice(0, 12).map((d, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "6px 0",
                    borderBottom: "1px solid #f1f1f1",
                  }}
                >
                  <div style={{ fontSize: 13 }}>
                    {new Date(d.monitored).toLocaleString()}
                  </div>
                  <div
                    style={{ display: "flex", gap: 12, alignItems: "center" }}
                  >
                    <div style={{ fontWeight: 700 }}>
                      {d.durationSeconds.toFixed(2)}s
                    </div>
                    <div
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: 6,
                        background: d.status === 0 ? "#4CAF50" : "#F44336",
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
